import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { Colorize } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 6
  }
}));

const TagSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").max(50, "Muito longo").required("Obrigatório"),
  color: Yup.string().min(4, "Inválida").max(9, "Inválida").required("Obrigatório")
});

const TagModal = ({ open, onClose, tagId }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    color: "#128C7E"
  };
  const [tag, setTag] = useState(initialState);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!tagId) return;

      try {
        const { data } = await api.get(`/tags/${tagId}`);
        setTag(prevState => ({ ...prevState, ...data }));
      } catch (error) {
        toastError(error);
      }
    })();

    return () => {
      setTag(initialState);
    };
  }, [tagId, open]);

  const handleClose = () => {
    onClose();
    setTag(initialState);
  };

  const handleSaveTag = async values => {
    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, values);
      } else {
        await api.post("/tags", values);
      }

      toast.success("Tag salva com sucesso");
      handleClose();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper" fullWidth maxWidth="sm">
        <DialogTitle>{tagId ? "Editar tag" : "Nova tag"}</DialogTitle>
        <Formik
          initialValues={tag}
          enableReinitialize
          validationSchema={TagSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveTag(values);
              actions.setSubmitting(false);
            }, 250);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label="Nome"
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                  fullWidth
                />
                <Field
                  as={TextField}
                  label="Cor"
                  name="color"
                  error={touched.color && Boolean(errors.color)}
                  helperText={touched.color && errors.color}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  onFocus={() => setColorPickerModalOpen(true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div
                          className={classes.colorAdornment}
                          style={{ backgroundColor: values.color }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton
                        size="small"
                        color="default"
                        onClick={() => setColorPickerModalOpen(true)}
                      >
                        <Colorize />
                      </IconButton>
                    )
                  }}
                />
                <ColorPicker
                  open={colorPickerModalOpen}
                  handleClose={() => setColorPickerModalOpen(false)}
                  onChange={color => {
                    values.color = color;
                    setTag(prevState => ({ ...prevState, color }));
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  Cancelar
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                  {tagId ? "Salvar alterações" : "Criar tag"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TagModal;
